// Stripe service for handling subscriptions
// This service handles client-side operations and calls Firebase Functions for server-side operations

const STRIPE_PUBLISHABLE_KEY = process.env.NODE_ENV === 'production' 
  ? 'pk_live_SAMPLE' // Replace with your live publishable key
  : 'pk_test_SAMPLE'; // Replace with your test publishable key

// Plan configurations
export const SUBSCRIPTION_PLANS = {
  BASIC: {
    id: 'basic_monthly',
    name: 'Basic',
    price: 9.99,
    currency: 'usd',
    buyUrl: 'https://buy.stripe.com/test_fZu00c3YtbXq5RA6G53Nm00',
    interval: 'month',
    features: [
      '10 monthly queries',
      '2-leg SGP suggestions',
      'Limited markets (Moneyline/Spread/Total)',
      '2 sportsbooks selectable',
      'Single-game scope only',
      'Best value list (top 5 results)',
      'EV badge vs AI fair',
      'No export; no alerts'
    ]
  },
  PRO: {
    id: 'pro_monthly',
    name: 'Pro',
    price: 29.99,
    currency: 'usd',
    buyUrl: 'https://buy.stripe.com/test_8x23cofHbgdGbbU2pP3Nm01',
    interval: 'month',
    features: [
      'Everything in Basic',
      '60 monthly queries',
      '2–3 leg SGP suggestions',
      'Wider markets (core player props)',
      '4 sportsbooks selectable',
      'Auto-broadening when lines are sparse',
      'Combo caps: up to 10 combos per run',
      'EV/Win%/Correlation badges',
      'Compare price (Offered vs AI fair)',
      'Save prompts + favorite books/markets',
      'Quick re-run functionality',
      'CSV export of results',
      'Email + chat support, 24h response'
    ]
  },
  ULTIMATE: {
    id: 'ultimate_monthly',
    name: 'Ultimate',
    price: 99.99,
    currency: 'usd',
    buyUrl: 'https://buy.stripe.com/test_3cIeV63Yt2mQ7ZI5C13Nm02',
    interval: 'month',
    features: [
      'Everything in Pro',
      '200 monthly queries',
      'Full market set (team + player props)',
      'Multi-game parlays (configurable)',
      '8+ sportsbooks selectable',
      'Priority pricing and broader fallback coverage',
      'Combo caps: up to 50 combos per run',
      'Configurable rules (avoid conflicts, min EV, min Win%)',
      'Custom alerts (email/SMS) on price movement',
      'EV threshold alerts',
      'Priority customer support',
      'Advanced analytics dashboard'
    ]
  }
};

// Initialize Stripe (this would typically be done in your app's root component)
export const initializeStripe = async () => {
  try {
    const { loadStripe } = await import('@stripe/stripe-js');
    return await loadStripe(STRIPE_PUBLISHABLE_KEY);
  } catch (error) {
    console.error('Error loading Stripe:', error);
    return null;
  }
};

// Get Stripe hosted checkout links via Firebase Functions
export const getCheckoutLinks = async () => {
  try {
    // Import Firebase Functions
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const { getApp } = await import('firebase/app');
    
    const app = getApp();
    const functions = getFunctions(app);
    const getCheckoutLinks = httpsCallable(functions, 'getCheckoutLinks');

    const result = await getCheckoutLinks();
    return result.data.checkoutLinks;
  } catch (error) {
    console.error('Error getting checkout links:', error);
    throw error;
  }
};

// Redirect to Stripe hosted checkout
export const redirectToCheckout = async (checkoutUrl) => {
  try {
    // Simply redirect to the hosted checkout URL
    window.location.href = checkoutUrl;
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

// Handle subscription creation
export const handleSubscription = async (planType, userEmail) => {
  try {
    const plan = SUBSCRIPTION_PLANS[planType];
    if (!plan) {
      throw new Error('Invalid plan type');
    }

    console.log(`Redirecting to hosted checkout for ${plan.name} plan`);
    console.log(`Price: $${plan.price}/${plan.interval}`);
    
    // Get checkout links
    const checkoutUrl = plan.buyUrl;

    const stripeUrl = `${checkoutUrl}?prefilled_email=${encodeURIComponent(userEmail)}`;

    
    if (!stripeUrl) {
      throw new Error(`No checkout link found for ${planType} plan`);
    }
    
    // Redirect to Stripe hosted checkout
    await redirectToCheckout(stripeUrl);
    
    return { success: true, plan: plan.name };
  } catch (error) {
    console.error('Subscription error:', error);
    throw error;
  }
};

// Get current subscription status (would typically come from your backend)
export const getSubscriptionStatus = async (customerId) => {
  try {
    // In a real application, this would fetch from your backend
    // which would query Stripe for the customer's subscription status
    const response = await fetch(`/api/subscription-status/${customerId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch subscription status');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching subscription status:', error);
    return null;
  }
};

// Cancel subscription
export const cancelSubscription = async (subscriptionId) => {
  try {
    const response = await fetch('/api/cancel-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscriptionId }),
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }

    return await response.json();
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};
